import { Badge } from "@shared/ui/badge";
import { Progress } from "@shared/ui/progress";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  CircleCheck,
  CirclePause,
  CircleX,
  PieChart,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../../shared/ui/card";
import {
  useConsumerDashboard,
  useConsumerListViewData,
  usePlanDashboard,
} from "../../hooks";
import { ConsumerFilter } from "./Consumerfilter";

interface ConsumerBreakdownTabProps {
  billCycleId: number;
  billDraftId: string;
}

interface ConsumerFilters {
  category: string;
  subCategory: string;
  area: string;
  subArea: string;
}

export const ConsumerBreakdownTab: React.FC<ConsumerBreakdownTabProps> = ({
  billCycleId,
  billDraftId,
}) => {
  console.log("billDraftId in ConsumerBreakdownTab ", billDraftId);

  const [searchParams, setSearchParams] = useSearchParams();

  // Read params from URL - now single values instead of arrays
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const category = searchParams.get("category") || "";
  const subCategory = searchParams.get("subCategory") || "";
  const area = searchParams.get("area") || "";
  const subArea = searchParams.get("subArea") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      search: searchQuery,
      category: category,
      subCategory: subCategory,
      area: area,
      subArea: subArea,
    }),
    [currentPage, searchQuery, category, subCategory, area, subArea]
  );

  const { data: planData } = usePlanDashboard(billCycleId);
  const { data: consumerListViewData } = useConsumerListViewData(
    billCycleId,
    billDraftId,
    currentPage,
    queryParams
  );
  const { data: consumerData } = useConsumerDashboard(billCycleId);

  // Page change handler
  const handlePageChange = (newPage: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: newPage.toString(),
    };
    setSearchParams(newParams);
  };

  // Search handler
  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    // Reset to page 1 on search
    newParams.page = "1";
    setSearchParams(newParams);
  };

  // Apply filters handler
  const handleApplyFilters = (filters: ConsumerFilters) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    // Add filter params
    if (filters.category) {
      newParams.category = filters.category;
    } else {
      delete newParams.category;
    }

    if (filters.subCategory) {
      newParams.subCategory = filters.subCategory;
    } else {
      delete newParams.subCategory;
    }

    if (filters.area) {
      newParams.area = filters.area;
    } else {
      delete newParams.area;
    }

    if (filters.subArea) {
      newParams.subArea = filters.subArea;
    } else {
      delete newParams.subArea;
    }

    // Reset to page 1 when filters change
    newParams.page = "1";
    setSearchParams(newParams);
  };

  // Reset filters handler
  const handleResetFilters = () => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.category;
    delete newParams.subCategory;
    delete newParams.area;
    delete newParams.subArea;
    newParams.page = "1";

    setSearchParams(newParams);
  };

  const hasActiveFilters = !!category || !!subCategory || !!area || !!subArea;

  const consumerListColumns = [
    {
      key: "consumerNo",
      header: "Account No",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      render: (value: any, row: any) => {
        return `${row.firstName} ${row.lastName}`;
      },
    },
    {
      key: "utilityService",
      header: "Utility Service",
      render: (value: any) => {
        return Array.isArray(value) ? value.join(", ") : "N/A";
      },
    },
    {
      key: "category",
      header: "Category",
    },
    {
      key: "premise",
      header: "Premise",
    },
    {
      key: "statusDisplay",
      header: "Status",
      render: (value: any, row: any) => {
        return (
          <Badge className={getStatusColor(row.statusDisplay)}>
            {row.statusDisplay}
          </Badge>
        );
      },
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      case "paused":
        return "bg-yellow-100 text-yellow-800";
      case "temporary disconnect":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getProgressColor = (category: any) => {
    switch (category?.toLowerCase()) {
      case "residential":
        return "bg-blue-600";
      case "commercial":
        return "bg-teal-500";
      case "industrial":
        return "bg-blue-700";
      default:
        return "bg-blue-500";
    }
  };

  const filterPanel = (
    <ConsumerFilter
      category={category}
      subCategory={subCategory}
      area={area}
      subArea={subArea}
      onApplyFilters={handleApplyFilters}
      onResetFilters={handleResetFilters}
    />
  );

  const activeFilterCount = [
    !!category,
    !!subCategory,
    !!area,
    !!subArea,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Existing dashboard cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-lg">Consumers by Category</CardTitle>
            </div>
            <CardDescription>
              Distribution of consumers across categories
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {consumerData?.categoryData?.map((item: any, index: number) => {
                const isPositive = Number(item?.percentage) > 0;
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm font-medium">
                          {item.category ?? "NA"}
                        </span>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Users className="h-3 w-3" />
                          {item.consumerCount}
                          {isPositive ? (
                            <TrendingUp className="h-3 w-3 text-green-600" />
                          ) : (
                            <TrendingDown className="h-3 w-3 text-red-600" />
                          )}
                          <span className="text-muted-foreground">
                            {item?.percentage}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <Progress
                      value={Number(item?.percentage)}
                      className="h-2"
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Cycle Comparison</CardTitle>
            <CardDescription>
              Consumer changes from previous cycle
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">
                    Previous Cycle
                  </div>
                  <div className="text-2xl font-bold">
                    {planData?.previousConsumerCount ?? "NA"}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">
                    New Consumers
                  </div>
                  <div className="text-2xl font-bold">
                    {planData?.newConsumerCount}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">Consumer Status</CardTitle>
          </div>
          <CardDescription>Current status of all consumers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium">Active</span>
              </div>
              <div className="text-2xl font-bold">{consumerData?.active}</div>
              <div className="text-xs text-muted-foreground">
                {consumerData?.activePercent}%
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CircleX className="h-4 w-4 text-red-600" />
                <span className="text-sm font-medium">Inactive</span>
              </div>
              <div className="text-2xl font-bold">{consumerData?.inactive}</div>
              <div className="text-xs text-muted-foreground">
                {consumerData?.inactivePercent}%
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CirclePause className="h-4 w-4 text-yellow-600" />
                <span className="text-sm font-medium">Paused</span>
              </div>
              <div className="text-2xl font-bold">{consumerData?.paused}</div>
              <div className="text-xs text-muted-foreground">
                {consumerData?.pausedPercent}%
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CircleX className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-xs">
                  Temporary Disconnect
                </span>
              </div>
              <div className="text-2xl font-bold">
                {consumerData?.disconnected}
              </div>
              <div className="text-xs text-muted-foreground">
                {consumerData?.disconnectedPercent}%
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Consumer List with Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Consumer List</CardTitle>
          <CardDescription>
            Complete list of consumers in the current cycle
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <GenericTable
            data={consumerListViewData?.results ?? []}
            columns={consumerListColumns}
            rowKey="id"
            emptyMessage="No consumers found"
            pagination={{
              currentPage: currentPage,
              totalPages: Math.ceil(
                (consumerListViewData?.count || 0) / PAGE_SIZE
              ),
              totalItems: consumerListViewData?.count || 0,
              itemsPerPage: PAGE_SIZE,
              startItem: (currentPage - 1) * PAGE_SIZE + 1,
              endItem: Math.min(
                currentPage * PAGE_SIZE,
                consumerListViewData?.count || 0
              ),
            }}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            searchPlaceholder="Search by account no, consumer name, premise..."
            filterPanel={filterPanel}
          />
        </CardContent>
      </Card>
    </div>
  );
};
